import { IEnergyOverview } from './types'
import { HeadingXSmall, LabelMedium, ParagraphXSmall } from 'baseui/typography';
import { GridHeaderCell } from '../../components/Grid'
import { LoadingView } from '../../../components/LoadingView'
import { useMemo } from 'react';
import { ICounter } from '../../../automationObjects/Devices/Counter';
import { CounterType } from '../../Devices/CounterType';
import { FormattedMessage, useIntl } from 'react-intl';
import { EnergyBarChart } from './';
import { useStyletron } from 'baseui';
import { useGetObjectsChildren } from '../../../hooks/useGetObjectsChildren';
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import RenderIfVisible from 'react-render-if-visible';
import { useMediaQuery } from '../../../hooks';
import { AutomationObjectsFlexGridView } from '../../components/Grid/AutomationObjectsFlexGridView';
import { ObjectHeader } from '../../components/ObjectHeader';
import { FaRegMoneyBillAlt } from 'react-icons/fa';
import { MdCo2 } from 'react-icons/md';
import { SIZE, StyledDivider } from 'baseui/divider';
import { Notification } from "baseui/notification";
import { Block } from 'baseui/block';
import { defaultCurrencyCode } from '../../../utils/currency';
import PowerOverview from './PowerOverview';
import { CounterMedium } from '../../Devices/CounterMedium';
import { themedUseStyletron } from '../../../theme/customTheme';

export function OperatorPanel(props: IEnergyOverview) {
    const intl = useIntl();
    const isSmallScreen = useMediaQuery('screen and (max-width: 600px)');
    const isHighRatio = useMediaQuery("only screen and (-webkit-min-device-pixel-ratio: 1.5)");
    const columnCount = isHighRatio ? [1, 1, 2, 3] : [1, 1, 2, 4];
    const [, theme] = useStyletron();
    const squareMeter = props.squereMeter;

    //const { objects:objects, error, isLoading, isSuccess } = useGetObjectsBySchemaFromParent(props.resourceGroupId, objectId, schema);
    const { children: objects, isLoading, isSuccess, error } = useGetObjectsChildren(props.resourceGroupId, props.id)

    const items = useMemo(() => objects?.sort((a: ICounter, b: ICounter) => a.counterMedium > b.counterMedium ? 1 : -1) ?? [], [objects]);
    const feedInMeters = useMemo(() => items?.filter((item, index) => item.counterType === CounterType.FeedIn) ?? [], [items])
    const feedOutMeters = useMemo(() => items?.filter((item, index) => item.counterType === CounterType.FeedOut) ?? [], [items])
    const productionMeters = useMemo(() => items?.filter((item, index) => item.counterType === CounterType.Producer) ?? [], [items])
    const consumerMeters = useMemo(() => items?.filter((item, index) => item.counterType === CounterType.Consumer) ?? [], [items])


    const allItems = [...feedInMeters, ...feedOutMeters]
    const countersFeedInFeedOutProduction = [...feedInMeters, ...feedOutMeters, ...productionMeters]
    const feedInAndOutMeters = [...feedInMeters, ...feedOutMeters]

    const powerGridInE = useMemo(() => feedInMeters?.filter((item, index) => item.counterMedium === CounterMedium.ElectricityMeter) ?? [], [feedInMeters])
    const powerGridOutE = useMemo(() => feedOutMeters?.filter((item, index) => item.counterMedium === CounterMedium.ElectricityMeter) ?? [], [feedOutMeters])
    const powerStorageInE = 0;
    const powerStorageOutE = 0;
    const powerPVE = useMemo(() => productionMeters?.filter((item, index) => item.counterMedium === CounterMedium.ElectricityMeter) ?? [], [productionMeters])

    const powerGridIn =  powerGridInE?.reduce((a,v) =>  a = a + v.actPower , 0 )
    const powerGridOut = powerGridOutE?.reduce((a,v) =>  a = a + v.actPower , 0 )
    const powerStorageIn = powerStorageInE;
    const powerStorageOut = powerStorageOutE;
    const powerPV = powerPVE?.reduce((a,v) =>  a = a + v.actPower , 0 )

    const currency = props.currencyCode ?? defaultCurrencyCode;
    return (
        <>
            <LoadingView isLoading={isLoading} error={error} isSucess={isSuccess}>

                <FlexGrid
                    flexGridColumnCount={columnCount}
                    flexGridColumnGap="scale800"
                    flexGridRowGap="scale800"
                >
                    {feedInMeters?.length > 0 && feedOutMeters?.length > 0 && productionMeters?.length > 0 &&
                        <FlexGridItem >
                            <RenderIfVisible defaultHeight={400} visibleOffset={500} stayRendered>

                                <EnergyHeader 
                                    description={intl.formatMessage({ id: "EnergyOverview.power", defaultMessage: "Power" })}
                                    name={intl.formatMessage({ id: "EnergyOverview.actpower", defaultMessage: "act Power" })} />
                                <PowerOverview powerGridIn ={powerGridIn > 0 ? powerGridIn : 0} powerGridOut ={powerGridOut < 0 ? powerGridOut : 0} powerStorageIn={powerStorageIn} powerStorageOut={powerStorageOut} powerPV={powerPV} dimension='kW' hasStorage={false}></PowerOverview>

                            </RenderIfVisible>
                        </FlexGridItem>
                    }
                    <FlexGridItem >
                        <RenderIfVisible defaultHeight={400} visibleOffset={500} stayRendered>

                            <EnergyHeader
                                description={intl.formatMessage({ id: "EnergyOverview.cost", defaultMessage: "Cost" })}
                                name={intl.formatMessage({ id: "EnergyOverview.last3months", defaultMessage: "Last 3 months" })} />
                            <EnergyBarChart showChart counters={allItems} squareMeter={squareMeter} propertyName='actCostsConsumption' currencyCode={currency} dimension=''></EnergyBarChart>

                        </RenderIfVisible>
                    </FlexGridItem>

                    <FlexGridItem >
                        <RenderIfVisible defaultHeight={400} visibleOffset={500} stayRendered>

                            <EnergyHeader 
                                description={intl.formatMessage({ id: "EnergyOverview.co2", defaultMessage: "CO2 footprint" })}
                                name={intl.formatMessage({ id: "EnergyOverview.last3months", defaultMessage: "Last 3 months" })} />
                            <EnergyBarChart showChart counters={allItems} squareMeter={squareMeter} propertyName='co2Consumption' dimension='kg'></EnergyBarChart>

                        </RenderIfVisible>
                    </FlexGridItem>
                    <FlexGridItem >
                        <RenderIfVisible defaultHeight={400} visibleOffset={500} stayRendered>

                            <EnergyHeader
                                description={intl.formatMessage({ id: "EnergyOverview.energy", defaultMessage: "Energy" })}
                                name={intl.formatMessage({ id: "EnergyOverview.last3months", defaultMessage: "Last 3 months" })} />
                            <EnergyBarChart showChart counters={allItems} squareMeter={squareMeter} propertyName='valueDelta' dimension='kWh'></EnergyBarChart>

                        </RenderIfVisible>
                    </FlexGridItem>
                    {feedInMeters?.length > 0 && feedOutMeters?.length > 0 && productionMeters?.length > 0 &&
                        <FlexGridItem >
                            <RenderIfVisible defaultHeight={400} visibleOffset={500} stayRendered>

                                <EnergyHeader
                                    description={intl.formatMessage({ id: "EnergyOverview.consumedEnergy", defaultMessage: "Consumed Energy in the building" })}
                                    name={intl.formatMessage({ id: "EnergyOverview.last3months", defaultMessage: "Last 3 months" })} />
                                <EnergyBarChart showChart counters={countersFeedInFeedOutProduction} squareMeter={squareMeter} propertyName='valueDelta' dimension='kWh'></EnergyBarChart>
 
                            </RenderIfVisible>
                        </FlexGridItem>
                    }
                </FlexGrid>
                <Block marginTop={theme.sizing.scale1200}>
                    <StyledDivider $size={SIZE.cell}></StyledDivider >
                </Block>
                <HeadingXSmall marginTop={theme.sizing.scale400}><FormattedMessage id="EnergyOverview.FeedInOutMeters" defaultMessage="Supply / feed-in meters" /></HeadingXSmall>
                {feedInAndOutMeters && feedInAndOutMeters.length > 0 ?
                    <AutomationObjectsFlexGridView noSort items={feedInAndOutMeters ?? []}></AutomationObjectsFlexGridView>
                    : <Notification kind='warning'><FormattedMessage id='no counters' defaultMessage="No 'Feed' or 'Produces' counters configured"></FormattedMessage></Notification>}

                {((productionMeters && productionMeters.length > 0) || (consumerMeters && consumerMeters.length > 0)) &&
                    <Block marginTop={theme.sizing.scale1200}>
                        {!isSmallScreen && <StyledDivider $size={SIZE.cell}></StyledDivider >}
                    </Block>
                }
                {productionMeters && productionMeters.length > 0 &&
                    <>
                        <HeadingXSmall marginTop={theme.sizing.scale400}><FormattedMessage id="EnergyOverview.Produced" defaultMessage="Produced" /></HeadingXSmall>
                        <AutomationObjectsFlexGridView noSort items={productionMeters ?? []}></AutomationObjectsFlexGridView>
                    </>
                }
                {consumerMeters && consumerMeters.length > 0 &&
                    <>
                        <GridHeaderCell header={<FormattedMessage id="EnergyOverview.OtherMeters" defaultMessage="Other meters" />}></GridHeaderCell>
                        <AutomationObjectsFlexGridView noSort items={consumerMeters ?? []}></AutomationObjectsFlexGridView>
                    </>
                }
            </LoadingView >
        </>
    );
}
function EnergyHeader({ description, name }: any) {
    const [css, theme] = themedUseStyletron();
return (
    <Block display='flex' flexDirection='row' alignItems='center' justifyContent='flex-start' gridGap='10px'>
        <div>
            <LabelMedium>{description}</LabelMedium>
            <ParagraphXSmall className={css({ textOverflow: 'ellipsis' })} margin={0}>{name}</ParagraphXSmall>
        </div>

    </Block>
)
}